import React from 'react';
import PropTypes from 'prop-types';
import { DragDropContext } from 'react-beautiful-dnd';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import LinearProgress from '@material-ui/core/LinearProgress';

import AddCircleIcon from '@material-ui/icons/AddCircle';

import BacklogList from './BacklogList';
import { multiSelectTo as multiSelect } from './BacklogUtils';
import AppContent from '../_Shared/Layout/AppContent';
import DataToolbar from '../_Shared/DataTables/DataToolbar';
import CreateModal from '../_Shared/CreateModal';
import Select from '../_Shared/SearchableDropdown';

const uuidv4 = require('uuid/v4');

const styles = theme => ({
    taskColumn: {
        display: 'flex'
    }, backlogContainer: {
        flexGrow: 1
    }
});
const defaultBacklogTask = { name: 'New Task', status: 'todo' }
const status = {
    'todo': 'To Do',
    'inprogress': 'In Progress',
    'complete': 'Completed'
}
const taskTypes = [
    { label: 'Personal', value: 'personal' },
    { label: 'Campaign', value: 'campaign' },
    { label: 'Project', value: 'project' }
]

class Backlog extends React.Component {

    static defaultProps = {
        advertisers: [], campaigns: []
    }
    state = {
        selectedTaskIds: [],
        draggingTaskId: null,
        showCreateModal: false,
        backlogTask: defaultBacklogTask
    };

    componentDidMount() {
        window.addEventListener('click', this.onWindowClick);
        window.addEventListener('keydown', this.onWindowKeyDown);
        window.addEventListener('touchend', this.onWindowTouchEnd);
    }

    componentWillUnmount() {
        window.removeEventListener('click', this.onWindowClick);
        window.removeEventListener('keydown', this.onWindowKeyDown);
        window.removeEventListener('touchend', this.onWindowTouchEnd);
    }

    onDragStart = (start) => {
        console.log(start);
        const id = start.draggableId;
        const selected = this.state.selectedTaskIds.find(
            taskId => taskId === id,
        );

        // if dragging an item that is not selected - unselect all items
        if (!selected) {
            this.unselectAll();
        }
        this.setState({
            draggingTaskId: start.draggableId,
        });
    };

    onDragEnd = (result) => {
        const destination = result.destination;
        //const source = result.source;

        // nothing to do
        if (!destination || result.reason === 'CANCEL') {
            this.setState({
                draggingTaskId: null,
            });
            return;
        }

        // const processed = mutliDragAwareReorder({
        //     entities: this.props.entities,
        //     selectedTaskIds: this.state.selectedTaskIds,
        //     source,
        //     destination,
        // });

        this.props.updateHandler(result);
        this.setState({ draggingTaskId: null, });

    };

    onWindowKeyDown = (event) => {
        if (event.defaultPrevented) {
            return;
        }

        if (event.key === 'Escape') {
            this.unselectAll();
        }
    };

    onWindowClick = (event) => {
        if (event.defaultPrevented) {
            return;
        }
        this.unselectAll();
    };

    onWindowTouchEnd = (event) => {
        if (event.defaultPrevented) {
            return;
        }
        this.unselectAll();
    };

    toggleSelection = (taskId) => {
        const selectedTaskIds = this.state.selectedTaskIds;
        const wasSelected = selectedTaskIds.includes(taskId);

        const newTaskIds = (() => {
            // Task was not previously selected
            // now will be the only selected item
            if (!wasSelected) {
                return [taskId];
            }

            // Task was part of a selected group
            // will now become the only selected item
            if (selectedTaskIds.length > 1) {
                return [taskId];
            }

            // task was previously selected but not in a group
            // we will now clear the selection
            return [];
        })();

        this.setState({
            selectedTaskIds: newTaskIds,
        });
    };

    toggleSelectionInGroup = (taskId) => {
        const selectedTaskIds = this.state.selectedTaskIds;
        const index = selectedTaskIds.indexOf(taskId);

        // if not selected - add it to the selected items
        if (index === -1) {
            this.setState({
                selectedTaskIds: [...selectedTaskIds, taskId],
            });
            return;
        }

        // it was previously selected and now needs to be removed from the group
        const shallow = [...selectedTaskIds];
        shallow.splice(index, 1);
        this.setState({
            selectedTaskIds: shallow,
        });
    };

    // This behaviour matches the MacOSX finder selection
    multiSelectTo = (newTaskId) => {
        const updated = multiSelect(
            this.state.entities,
            this.state.selectedTaskIds,
            newTaskId,
        );

        if (updated == null) {
            return;
        }

        this.setState({
            selectedTaskIds: updated,
        });
    };

    unselect = () => {
        this.unselectAll();
    };

    unselectAll = () => {
        this.setState({
            selectedTaskIds: [],
        });
    };

    toggleCreateModal = e => {
        const { showCreateModal } = this.state;
        this.setState({ showCreateModal: !showCreateModal, backlogTask: defaultBacklogTask });
    }

    handleInputChange = (name, value) => {
        this.setState((prevState, state) => {
            return { backlogTask: Object.assign({}, prevState.backlogTask, { [name]: value }) }
        });
    }
   
    createTask = e => {
        const { backlogTask } = this.state;
        this.props.saveHandler(Object.assign({}, backlogTask, { id: uuidv4().replace(/-/g, '') }));
        this.setState({ showCreateModal: false });
    }

    render() {

        const { classes, columns, tasks, loading, advertisers, campaigns } = this.props;
        const { selectedTaskIds: selected, showCreateModal, backlogTask } = this.state;
        return (
            <AppContent>
                {
                    loading && <LinearProgress variant="query" />
                }
                <DataToolbar title="Backlog" numSelected={0} inset={true}>
                    <Tooltip title="Add New">
                        <IconButton onClick={this.toggleCreateModal} color="secondary"><AddCircleIcon /></IconButton>
                    </Tooltip>
                </DataToolbar>
                <DragDropContext onDragStart={this.onDragStart} onDragEnd={this.onDragEnd} >
                    <Grid className={classes.backlogContainer} container spacing={40}>
                        {
                            columns.map((column, colIndex) => (
                                <Grid item xs key={`col_${colIndex}`} className={classes.taskColumn}>
                                    <BacklogList
                                        column={column} userAssignmentHandler={this.props.userAssignmentHandler}
                                        tasks={[].concat(tasks).filter(x => x.status === column.id)}
                                        selectedTaskIds={selected}
                                        draggingTaskId={this.state.draggingTaskId}
                                        toggleSelection={this.toggleSelection}
                                        toggleSelectionInGroup={this.toggleSelectionInGroup}
                                        multiSelectTo={this.multiSelectTo}
                                    />
                                </Grid>
                            ))
                        }
                    </Grid>
                </DragDropContext>
                <CreateModal doubleWidth title="Create Task" open={showCreateModal} closeHandler={this.toggleCreateModal} saveHandler={this.createTask}>
                    <Grid container spacing={24}>
                        <Grid item xs>
                            <Grid container item xs spacing={24}>
                                <Grid item xs>
                                    <TextField autoFocus value={backlogTask.name} onChange={e => this.handleInputChange('name', e.target.value)} id="name" name="name" label="Task Name" type="text" fullWidth />
                                </Grid>
                            </Grid>
                            <Grid container item spacing={24}>
                                <Grid item xs>
                                    <InputLabel>Status: {status[backlogTask.status]}</InputLabel>
                                </Grid>

                            </Grid>
                            <Grid container item spacing={24}>
                                <Grid item xs>
                                    <TextField multiline rows={5} value={backlogTask.details}
                                        onChange={e => this.handleInputChange('details', e.target.value)}
                                        id="details" name="details" label="Details" type="text" fullWidth />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs>
                            <Grid container item spacing={24}>
                                <Grid item xs>
                                    <Select options={taskTypes} value={backlogTask.type} name="type" label="Task Type"
                                        onChange={value => this.handleInputChange('type', value)} placeholder={"Please select a Task Type"} />
                                </Grid>
                            </Grid>
                            {
                                backlogTask.type && <React.Fragment>
                                    <Grid container item spacing={24}>
                                        <Grid item xs>
                                            {
                                                backlogTask.type === 'personal' && <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={backlogTask.public}
                                                            onChange={event => this.handleInputChange('public', event.target.value)}
                                                            value="public"
                                                        />
                                                    }
                                                    label="Public"
                                                />
                                            }
                                            {
                                                backlogTask.type === 'campaign' && <Select options={[].concat(advertisers).map(x => ({ label: x.name, value: x.id }))}
                                                    value={backlogTask.advertiserId} name="advertiserId" label="Advertiser"
                                                    onChange={value => this.handleInputChange('advertiserId', value)} placeholder={"Please select a Advertiser"} />
                                            }
                                            {
                                                backlogTask.type === 'project' && <Select options={taskTypes}
                                                    value={backlogTask.projectId} name="projectId" label="Project"
                                                    onChange={value => this.handleInputChange('projectId', value)} placeholder={"Please select a Project"} />
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid container item spacing={24}>
                                        <Grid item xs>
                                            {
                                                backlogTask.type === 'project' && <Select options={[].concat(advertisers).map(x => ({ label: x.name, value: x.id }))}
                                                    value={backlogTask.advertiserId} name="advertiserId" label="Advertiser"
                                                    onChange={value => this.handleInputChange('advertiserId', value)} placeholder={"Please select a Advertiser"} />
                                            }
                                            {
                                                backlogTask.type === 'campaign' && <Select options={[].concat(campaigns).filter(x =>
                                                    x.advertiserId === backlogTask.advertiserId).map(x => ({ label: x.name, value: x.id }))}
                                                    value={backlogTask.campaignId} name="campaignId" label="Campaign"
                                                    onChange={value => this.handleInputChange('campaignId', value)} placeholder={"Please select a Campaign"} />
                                            }
                                        </Grid>
                                    </Grid>
                                </React.Fragment>
                            }
                        </Grid>
                    </Grid>
                </CreateModal>
            </AppContent>)
    }
}

Backlog.propTypes = {
    classes: PropTypes.object.isRequired
}

export default withStyles(styles, { withTheme: true })(Backlog);


// WEBPACK FOOTER //
// ./src/Components/Presentation/BacklogTasks/Backlog.js